import React, { useState } from 'react';
import { Button, Modal, Input, Typography, Tooltip } from 'antd';
import { CommentOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

type Props = {
  address: string;
  existingComment: string | null;
  onSave: (comment: string) => void;
};

const CommentModal = ({ address, existingComment, onSave }: Props): JSX.Element => {
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [comment, setComment] = useState(existingComment ?? '');

  const handleEditOpen = () => {
    setComment(existingComment ?? '');
    setEditOpen(true);
  };

  const handleSave = () => {
    onSave(comment.trim());
    setEditOpen(false);
  };

  const handleCancel = () => {
    setComment(existingComment ?? '');
    setEditOpen(false);
  };

  const hasComment = Boolean(existingComment?.trim());

  return (
    <>
      {hasComment ? (
        <div className="route-details__comment-cell">
          <Text ellipsis={{ tooltip: false }} className="route-details__comment-text">
            {existingComment}
          </Text>
          <div className="route-details__comment-actions">
            <Tooltip title="View comment">
              <Button
                type="text"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => setViewOpen(true)}
                className="comment-modal__view-btn c-blue"
                size="small"
              />
            </Tooltip>
            <Tooltip title="Edit comment">
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined />}
                onClick={handleEditOpen}
                className="comment-modal__edit-btn c-blue"
                size="small"
              />
            </Tooltip>
          </div>
        </div>
      ) : (
        <Button
          type="link"
          icon={<CommentOutlined />}
          onClick={handleEditOpen}
          style={{ padding: 0, color: '#52c41a' }}
        >
          Add
        </Button>
      )}

      <Modal
        title={
          <div>
            <div className="comment-modal__title">Comment</div>
            <Text type="secondary" className="comment-modal__address" ellipsis={{ tooltip: address }}>
              {address}
            </Text>
          </div>
        }
        visible={viewOpen}
        onCancel={() => setViewOpen(false)}
        footer={
          <div className="comment-modal__footer-actions">
            <Button onClick={() => setViewOpen(false)}>Close</Button>
            <Button
              type="primary"
              onClick={() => {
                setViewOpen(false);
                handleEditOpen();
              }}
            >
              Edit
            </Button>
          </div>
        }
        destroyOnClose
      >
        <div style={{ padding: '8px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{existingComment}</div>
      </Modal>

      <Modal
        title={
          <div>
            <div className="comment-modal__title">Edit Comment</div>
            <Text type="secondary" className="comment-modal__address" ellipsis={{ tooltip: address }}>
              {address}
            </Text>
          </div>
        }
        visible={editOpen}
        onCancel={handleCancel}
        destroyOnClose
        footer={
          <div className="comment-modal__footer">
            {hasComment && (
              <Button
                danger
                type="text"
                onClick={() => {
                  onSave('');
                  setEditOpen(false);
                }}
              >
                Remove Comment
              </Button>
            )}
            <div className="comment-modal__footer-actions">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" onClick={handleSave} disabled={comment.trim() === (existingComment ?? '')}>
                Save
              </Button>
            </div>
          </div>
        }
      >
        <TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment for this location..."
          rows={4}
          maxLength={500}
          showCount
          autoFocus
          className="comment-modal__textarea"
        />
      </Modal>
    </>
  );
};

export default CommentModal;
